import * as React from 'react';
import {Query, Mutation} from 'react-apollo';

// @ts-expect-error - imported from .js/.jsx file
import {LoadingIcon} from 'shells/icon/svg/loading-icon';

import type {OutcomeType} from 'nutshell-graphql-types';
import type {
    CreateAccountAssignmentAutomation as CreateAccountAssignmentAutomationMutation,
    CreateAccountAssignmentAutomationVariables as CreateAccountAssignmentAutomationMutationVariables,
    CreateClosedLeadAccountTypeAutomationInputFieldInput,
    DeleteAccountAssignmentAutomation as DeleteAccountAssignmentAutomationMutation,
    DeleteAccountAssignmentAutomationVariables as DeleteAccountAssignmentAutomationMutationVariables,
    DeleteClosedLeadAccountTypeAutomationInputFieldInput,
    UpdateAccountAssignmentAutomation as UpdateAccountAssignmentAutomationMutation,
    UpdateAccountAssignmentAutomationVariables as UpdateAccountAssignmentAutomationMutationVariables,
    UpdateClosedLeadAccountTypeAutomationInputFieldInput,
    GetAccountAssignmentAutomation as GetAccountAssignmentAutomationQuery,
    GetAccountAssignmentAutomationVariables as GetAccountAssignmentAutomationQueryVariables,
    AccountTypeAutomation as AccountTypeAutomationFragment,
} from 'nutshell-graphql-types';

import {useGetSessionUser} from '../../../core/use-get-session-user';

import GET_ACCOUNT_ASSIGNMENT_AUTOMATION from '../../graphql/queries/get-account-assignment-automation.graphql';

import {FormAccountAssignmentsComponent} from './form-account-assignments-component';
import CREATE_ACCOUNT_ASSIGNMENT_AUTOMATION from './graphql/mutations/create-account-assignment-automation.graphql';
import DELETE_ACCOUNT_ASSIGNMENT_AUTOMATION from './graphql/mutations/delete-account-assignment-automation.graphql';
import UPDATE_ACCOUNT_ASSIGNMENT_AUTOMATION from './graphql/mutations/update-account-assignment-automation.graphql';

import styles from '../sales-process-form.module.css';

type Props = OwnProps & {
    accountTypeAutomation: AccountTypeAutomationFragment | null | undefined;
    isLoadingAccountTypeAutomation: boolean;
    createAccountAssignment: (
        arg1: CreateClosedLeadAccountTypeAutomationInputFieldInput
    ) => Promise<any>;
    updateAccountAssignment: (
        arg1: UpdateClosedLeadAccountTypeAutomationInputFieldInput
    ) => Promise<any>;
    deleteAccountAssignment: (
        arg1: DeleteClosedLeadAccountTypeAutomationInputFieldInput
    ) => Promise<any>;
    canAccessSetup: boolean;
};

class FormAccountAssignmentsGraphQLComponent extends React.PureComponent<Props> {
    render() {
        if (!this.props.isLoadingAccountTypeAutomation) {
            return (
                <FormAccountAssignmentsComponent
                    entity={this.props.accountTypeAutomation}
                    formSubmitBtnRef={this.props.formSubmitBtnRef}
                    onDelete={this.deleteForm}
                    onSaveForm={this.saveForm}
                    canAccessSetup={this.props.canAccessSetup}
                />
            );
        }

        // TODO: Need a loading state
        return (
            <div className={styles['loading-container']}>
                <LoadingIcon size={48} />
            </div>
        );
    }

    saveForm = (accountTypeId: string) => {
        if (this.props.entityId) {
            return this.props.updateAccountAssignment({
                id: this.props.entityId,
                accountTypeId: accountTypeId,
            });
        }

        return this.props.createAccountAssignment({
            accountTypeId: accountTypeId,
            outcomeType: this.props.parentId,
            stagesetId: this.props.stagesetId,
        });
    };

    deleteForm = () => {
        if (this.props.entityId) {
            return this.props.deleteAccountAssignment({
                id: this.props.entityId,
            });
        }

        return Promise.reject('Cannot delete an account assignment automation with an entityId');
    };
}

class CreateAccountAssignmentMutation extends Mutation<
    CreateAccountAssignmentAutomationMutation,
    CreateAccountAssignmentAutomationMutationVariables
> {}

class DeleteAccountTypeAutomation extends Mutation<
    DeleteAccountAssignmentAutomationMutation,
    DeleteAccountAssignmentAutomationMutationVariables
> {}

class UpdateAccountTypeAutomationMutation extends Mutation<
    UpdateAccountAssignmentAutomationMutation,
    UpdateAccountAssignmentAutomationMutationVariables
> {}

class AccountAssignmentAutomationQuery extends Query<
    GetAccountAssignmentAutomationQuery,
    GetAccountAssignmentAutomationQueryVariables
> {}

type OwnProps = {
    entityId: string | null | undefined;
    // The stage id
    parentId: OutcomeType;
    stagesetId: string;
    formSubmitBtnRef: (arg1?: HTMLInputElement | null | undefined) => any;
};

export const FormAccountAssignments = (props: OwnProps) => {
    const {user} = useGetSessionUser();

    return (
        <CreateAccountAssignmentMutation mutation={CREATE_ACCOUNT_ASSIGNMENT_AUTOMATION}>
            {(createAccountAssignment) => (
                <DeleteAccountTypeAutomation mutation={DELETE_ACCOUNT_ASSIGNMENT_AUTOMATION}>
                    {(deleteAccountAssignment) => (
                        <UpdateAccountTypeAutomationMutation
                            mutation={UPDATE_ACCOUNT_ASSIGNMENT_AUTOMATION}
                        >
                            {(updateAccountAssignment) => (
                                <AccountAssignmentAutomationQuery
                                    query={GET_ACCOUNT_ASSIGNMENT_AUTOMATION}
                                    //
                                    variables={{
                                        automationId:
                                            props.entityId || '' /* skip prop makes this safe */,
                                    }}
                                    skip={!props.entityId}
                                >
                                    {({data, loading}) => {
                                        const accountTypeAutomation = data
                                            ? data.accountTypeAutomation
                                            : null;

                                        return (
                                            <FormAccountAssignmentsGraphQLComponent
                                                stagesetId={props.stagesetId}
                                                parentId={props.parentId}
                                                entityId={props.entityId}
                                                formSubmitBtnRef={props.formSubmitBtnRef}
                                                accountTypeAutomation={accountTypeAutomation}
                                                isLoadingAccountTypeAutomation={loading}
                                                createAccountAssignment={(automation) =>
                                                    createAccountAssignment({
                                                        variables: {automation},
                                                    })
                                                }
                                                updateAccountAssignment={(automation) =>
                                                    updateAccountAssignment({
                                                        variables: {automation},
                                                    })
                                                }
                                                deleteAccountAssignment={(automation) =>
                                                    deleteAccountAssignment({
                                                        variables: {automation},
                                                    })
                                                }
                                                canAccessSetup={Boolean(
                                                    user && user.permissions.canAccessSetup
                                                )}
                                            />
                                        );
                                    }}
                                </AccountAssignmentAutomationQuery>
                            )}
                        </UpdateAccountTypeAutomationMutation>
                    )}
                </DeleteAccountTypeAutomation>
            )}
        </CreateAccountAssignmentMutation>
    );
};
